import { useState, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import {
  Vendor,
  RawMaterial,
} from "../../models/RawMaterials/RawMaterial-model";
import RawMaterialService, {
  getRawMaterialCategories,
  getRawMaterialMeasurementype,
} from "../../services/RawMaterials/RawMaterialService";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import { useNavigate } from "react-router-dom";

const RawMaterialPurchaseForm = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [Pos, setPos] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<any[]>([]);
  const [measurementType, setMeasurementType] = useState<string>("/unit");
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [form, setForm] = useState({
    vendorID: 0,
    purchaseDate: new Date(),
    invoiceId: "",
    id: 0,
    posID: 0,
    quantity: 0,
    basicAmount: 0,
    taxAmount: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  // Fetch dropdown data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vendorList, rawMaterialList, POSlist] = await Promise.all([
          RawMaterialService.getVendorList(),
          RawMaterialService.getRawMaterials(),
          oilProductionService.GetMUPlantName(),
        ]);
        setVendors(vendorList);
        setRawMaterials(rawMaterialList);
        setPos(POSlist);
      } catch (error) {
        showToast("error", "Error", "Failed to load initial data");
      }
    };
    fetchData();
  }, []);

  const showToast = (
    severity: "success" | "error" | "warn",
    summary: string,
    detail: string
  ) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const handleChange = (field: string, value: any) => {
    if (field === "invoiceId") {
      const sanitized = (value ?? "").toString().replace(/[^a-zA-Z0-9]/g, "");
      setForm((prev) => {
        const updated = { ...prev, invoiceId: sanitized };
        return updated;
      });
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.invoiceId;
        return newErrors;
      });
      return;
    }

    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "basicAmount" || field === "taxAmount") {
        const newTotal = (updated.basicAmount ?? 0) + (updated.taxAmount ?? 0);
        setTotalAmount(newTotal);
      }

      return updated;
    });
  };

  const fieldHoverStyle = (fieldName: string) => ({
    border:
      hoveredField === fieldName ? "2px solid #6366F1" : "1px solid #ced4da",
    boxShadow:
      hoveredField === fieldName ? "0 0 6px rgba(99, 102, 241, 0.3)" : "none",
    transition: "all 0.2s ease-in-out",
    width: "100%",
    minHeight: "40px",
  });

  const handleRawMaterialChange = async (e: DropdownChangeEvent) => {
    const materialId = e.value;
    handleChange("id", materialId);

    if (!materialId) {
      setSelectedCategory([]);
      setMeasurementType("/unit");
      return;
    }

    try {
      const category = await getRawMaterialCategories(materialId);
      setSelectedCategory(category || []);
      const measurementArray = await getRawMaterialMeasurementype(materialId);
      if (Array.isArray(measurementArray) && measurementArray.length > 0) {
        setMeasurementType(measurementArray[0].measurementTypeName ?? "/unit");
      } else {
        setMeasurementType("/unit");
      }
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!form.vendorID) newErrors.vendorID = "Vendor is required";
    if (!form.purchaseDate)
      newErrors.purchaseDate = "Purchase Date is required";

    if (!form.invoiceId) {
      newErrors.invoiceId = "Invoice Number is required";
    } else {
      const regex = /^(?=.*\d)[A-Za-z0-9]+$/;
      if (!regex.test(form.invoiceId)) {
        newErrors.invoiceId =
          "Invoice Number must be alphanumeric and contain at least one number";
      }
    }

    if (!form.id) newErrors.id = "Raw Material is required";
    if (!form.posID) newErrors.posID = "Manufacturing Unit is required";
    if (form.quantity <= 0)
      newErrors.quantity = "Quantity must be greater than 0";
    if (form.basicAmount <= 0)
      newErrors.basicAmount = "Base Price must be greater than 0";
    if (form.taxAmount < 0) newErrors.taxAmount = "Tax cannot be negative";

    // Tax amount should not be equal to or exceed base price
    if (form.basicAmount > 0 && form.taxAmount >= form.basicAmount) {
      newErrors.taxAmount = "Tax amount must be less than base price";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast("error", "Validation Error", "Please fix all errors before submitting");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        rawMaterialId: form.id,
        vendorid: form.vendorID,
        purchaseDate: form.purchaseDate.toISOString(),
        invoiceId: form.invoiceId.toString(),
        quantity: form.quantity ?? 0,
        categoryId: selectedCategory?.[0]?.categoryID || 0,
        posId: form.posID,
        basicAmount: form.basicAmount,
        taxAmount: form.taxAmount,
        totalAmount: totalAmount,
      };

      const response = await RawMaterialService.createRawMaterialPurchase(
        payload
      );
      if (response?.data?.isSuccess) {
        showToast("success", "Success", response.data.message);
        navigate("/RawMaterialdatatable");
      } else {
        showToast(
          "error",
          "Error",
          response?.data?.message || "Operation failed"
        );
      }
    } catch (error) {
      showToast("error", "Error", "Failed to submit. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle tax amount change with validation
  const handleTaxAmountChange = (value: number | null) => {
    const taxValue = value ?? 0;

    // Check if tax amount is equal to or exceeds base price
    if (taxValue >= form.basicAmount && form.basicAmount > 0) {
      setErrors((prev) => ({
        ...prev,
        taxAmount: "Tax amount must be less than base price",
      }));
      showToast("warn", "Invalid Input", "Tax amount must be less than base price");
      return; // Don't update the value
    } else {
      // Clear the error if validation passes
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.taxAmount;
        return newErrors;
      });
    }

    handleChange("taxAmount", taxValue);
    setTotalAmount((form.basicAmount ?? 0) + taxValue);
  };

  // Handle base price change and validate existing tax amount
  const handleBasePriceChange = (value: number | null) => {
    const baseValue = value ?? 0;

    handleChange("basicAmount", baseValue);
    setTotalAmount(baseValue + (form.taxAmount ?? 0));

    // Re-validate tax amount when base price changes
    if (form.taxAmount >= baseValue && baseValue > 0) {
      setErrors((prev) => ({
        ...prev,
        taxAmount: "Tax amount must be less than base price",
      }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.taxAmount;
        return newErrors;
      });
    }
  };

  // Check if form has any errors or required fields are empty
  const isFormInvalid = () => {
    return (
      Object.keys(errors).length > 0 ||
      !form.vendorID ||
      !form.purchaseDate ||
      !form.invoiceId ||
      !form.id ||
      !form.posID ||
      form.quantity <= 0 ||
      form.basicAmount <= 0 ||
      form.taxAmount < 0 ||
      (form.basicAmount > 0 && form.taxAmount >= form.basicAmount)
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="fixed inset-0 flex justify-center items-center  mx-8">
        <div className="w-full max-w-2xl bg-white shadow-lg rounded-lg  pt-4 px-8 pb-8 mx-8">
          <h5 className="mb-3 text-white text-center font-semibold bg-green-700 py-2 rounded">
            RAW MATERIAL PURCHASE
          </h5>

          <form onSubmit={handleSubmit}>
            <div className="grid">
              {/* Purchase Date */}
              <div className=" col-12 md:col-6">
                <label className="font-semibold block ">Purchase Date*</label>
                <Calendar
                  value={form.purchaseDate}
                  onChange={(e) => handleChange("purchaseDate", e.value)}
                  dateFormat="dd-mm-yy"
                  showIcon
                  className="w-full"
                  style={fieldHoverStyle("description")}
                />
                {errors.purchaseDate && (
                  <small className="p-error">{errors.purchaseDate}</small>
                )}
              </div>

              {/* Raw Material */}
              <div className=" col-12 md:col-6">
                <label className="font-semibold block ">Raw Material*</label>
                <Dropdown
                  options={rawMaterials}
                  optionLabel="name"
                  optionValue="id"
                  value={form.id}
                  onChange={handleRawMaterialChange}
                  className="w-full"
                  style={fieldHoverStyle("description")}
                  filter
                  filterMatchMode="startsWith"
                  showFilterClear
                />
                {errors.id && <small className="p-error">{errors.id}</small>}
              </div>

              {/* Manufacturing Unit */}
              <div className=" col-12 md:col-6">
                <label className="font-semibold block ">
                  Manufacturing Unit*
                </label>
                <Dropdown
                  options={Pos.map((pos, index) => ({
                    label: `Unit-${index + 1}-${index === 0 ? 'KR' : 'MT'}`,
                    value: pos.posID
                  }))}
                  optionLabel="label"
                  optionValue="value"
                  value={form.posID}
                  onChange={(e: DropdownChangeEvent) =>
                    handleChange("posID", e.value)
                  }
                  className="w-full"
                  style={fieldHoverStyle("description")}
                />
                {errors.posID && (
                  <small className="p-error">{errors.posID}</small>
                )}
              </div>

              {/* Vendor */}
              <div className=" col-12 md:col-6">
                <label className="font-semibold block ">Vendor*</label>
                <Dropdown
                  options={vendors}
                  optionLabel="firmInfo"
                  optionValue="vendorID"
                  value={form.vendorID}
                  onChange={(e: DropdownChangeEvent) =>
                    handleChange("vendorID", e.value)
                  }
                  className="w-full"
                  style={fieldHoverStyle("description")}
                />
                {errors.vendorID && (
                  <small className="p-error">{errors.vendorID}</small>
                )}
              </div>

              {/* Invoice Number */}
              <div className=" col-12 md:col-6">
                <label className="font-semibold block ">Invoice Number*</label>
                <InputText
                  value={form.invoiceId}
                  onChange={(e) => handleChange("invoiceId", e.target.value)}
                  className="w-full"
                  style={fieldHoverStyle("description")}
                />
                {errors.invoiceId && (
                  <small className="p-error">{errors.invoiceId}</small>
                )}
              </div>

              {/* Invoiced Quantity */}
              <div className=" col-12 md:col-6">
                <label className="font-semibold block ">
                  Invoiced Quantity*
                </label>
                <div className="p-inputgroup">
                  <InputNumber
                    value={form.quantity}
                    onChange={(e) => handleChange("quantity", e.value ?? 0)}
                    min={1}
                    className="w-full"
                    style={fieldHoverStyle("description")}
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    useGrouping={false}
                  />
                  <span className="p-inputgroup-addon">{measurementType}</span>
                </div>
                {errors.quantity && (
                  <small className="p-error">{errors.quantity}</small>
                )}
              </div>

              {/* Base Price */}
              <div className=" col-12 md:col-4">
                <label className="font-semibold block ">Base Price (₹)*</label>
                <div className="p-inputgroup">
                  <InputNumber
                    value={form.basicAmount}
                    onChange={(e) => handleBasePriceChange(e.value)}
                    mode="currency"
                    currency="INR"
                    locale="en-IN"
                    min={0.01}
                    className="w-full"
                    style={fieldHoverStyle("description")}
                  />
                </div>
                {errors.basicAmount && (
                  <small className="p-error">{errors.basicAmount}</small>
                )}
              </div>
              {/* Tax Amount */}
              <div className=" col-12 md:col-4">
                <label className="font-semibold block ">Tax Amount (₹)*</label>
                <div className="p-inputgroup">
                  <InputNumber
                    value={form.taxAmount}
                    onChange={(e) => handleTaxAmountChange(e.value)}
                    mode="currency"
                    currency="INR"
                    locale="en-IN"
                    min={0}
                    className="w-full"
                    style={fieldHoverStyle("description")}
                  />
                </div>
                {errors.taxAmount && (
                  <small className="p-error">{errors.taxAmount}</small>
                )}

              </div>
              {/* Total Amount */}
              <div className=" col-12 md:col-4">
                <label className="font-semibold block ">Total Amount (₹)</label>
                <div className="p-inputgroup">
                  <InputNumber
                    value={totalAmount}
                    mode="currency"
                    readOnly
                    currency="INR"
                    locale="en-IN"
                    className="w-full"
                    style={fieldHoverStyle("description")}
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mx-8">
              <div className="mx-8">
                <div className="mx-8">
                  <div className="w-full flex justify-center mt-2 mx-8">
                    <Button
                      type="submit"
                      label={submitting ? "Submitting..." : "Submit"}
                      icon={submitting ? "pi pi-spinner pi-spin" : undefined}
                      disabled={submitting || isFormInvalid()}
                      className="p-button-success"
                      style={{ width: "150px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default RawMaterialPurchaseForm;